import type { FileShareClient } from "@anna-money/anna-web-lib/api";
import { makeAutoObservable, runInAction } from "mobx";
import { DIRECTOR_FIELDS_LIST } from "components/IncorporationRegisterForm/components/DirectorStep/components/DirectorCheck/components/DirectorCard/fields";
import { FormSubmitMethod } from "helpers/decorators/formSubmitMethod";
import type { AddressFormValue } from "helpers/forms/addressTypes";
import type { AnalyticsManager } from "services/analytics/analyticsManager";
import type { AttachmentStore } from "services/attachments/attachmentStore";
import type { DirectorAddressStore } from "services/director/directorAddressStore";
import type { DirectorStore } from "services/director/directorStore";
import type { IncorporationStore } from "services/incorporation/incorporationStore";
import type { IncorporationRegisterFormStepsStore } from "services/incorporationRegisterForm/incorporationRegisterFormStepsStore";
import type { IIssuesStore } from "services/issues/issuesStore";
import { FormError } from "types/errors";
import type { DirectorAddressFormTypes, ExtendedDirectorData } from "./rejectionTypes";

export enum RejectAddressStep {
  List = "List",
  Address = "Address",
}

const STEPS = [RejectAddressStep.List, RejectAddressStep.Address];

export class RejectionAddressStore {
  private _activeStep: RejectAddressStep = RejectAddressStep.List;
  private _activeDirector: ExtendedDirectorData | null = null;
  private _uploadedProofFile?: File;
  private _uploadedProofFileId?: string;
  private _error?: string;
  private _extendedDirectorsList: ExtendedDirectorData[] = [];

  constructor(
    private _directorStore: DirectorStore,
    private _directorAddressStore: DirectorAddressStore,
    private readonly _fileShareClient: FileShareClient,
    private readonly _attachmentStore: AttachmentStore,
    private _analyticsManager: AnalyticsManager,
    private _incorporationStore: IncorporationStore,
    private _registerStepStore: IncorporationRegisterFormStepsStore,
    private readonly _issuesStore: IIssuesStore,
    private _directorId?: string,
  ) {
    makeAutoObservable(this);
    this._initializeExtendedDirectorsList();
  }

  private _initializeExtendedDirectorsList(): void {
    this._extendedDirectorsList = this._directorStore.directors.map((director) => ({
      ...director,
      isVerified: false,
    }));
  }
  get activeStep(): RejectAddressStep {
    return this._activeStep;
  }

  set activeStep(step: RejectAddressStep) {
    this._activeStep = step;
    this._trackStepView(step);
  }

  private _trackStepView(step: RejectAddressStep): void {
    this._analyticsManager.event("rejection_flow_step_viewed", {
      step,
      incorporation_id: this._incorporationStore.incorporation?.id,
      total_directors: this._extendedDirectorsList.length,
      verified_directors_count: this._extendedDirectorsList.filter((d) => d.isVerified).length,
    });
  }

  get directorsList(): ExtendedDirectorData[] {
    // Sync with current director store state if needed
    if (this._extendedDirectorsList.length !== this._directorStore.directors.length) {
      this._initializeExtendedDirectorsList();
    }
    return this._extendedDirectorsList;
  }

  get isFirstStep(): boolean {
    return this.activeStep === STEPS[0];
  }

  get isLastStep(): boolean {
    return this.activeStep === STEPS.at(-1);
  }

  get activeDirector(): ExtendedDirectorData | null {
    return this._activeDirector;
  }

  set activeDirector(director: ExtendedDirectorData | null) {
    this._activeDirector = director;
    if (director) {
      this._analyticsManager.event("rejection_flow_director_selected", {
        incorporation_id: this._incorporationStore.incorporation?.id,
        is_already_verified: director.isVerified,
        director_index: this._extendedDirectorsList.findIndex((d) => d.id === director.id) + 1,
        total_directors: this._extendedDirectorsList.length,
      });
    }
  }

  get uploadedProofFile(): File | undefined {
    return this._uploadedProofFile;
  }

  get uploadedProofFileId(): string | undefined {
    return this._uploadedProofFileId;
  }

  get error(): string | undefined {
    return this._error;
  }

  get directorFullName(): string {
    if (!this._activeDirector) {
      return "";
    }
    const field = DIRECTOR_FIELDS_LIST[0];
    const formatted = field && typeof field.formatter === "function" ? field.formatter(this._activeDirector) : "";
    return typeof formatted === "string" ? formatted : formatted ? String(formatted) : "";
  }

  get currentAddress(): AddressFormValue | undefined {
    // Get the active director's address from director address store
    if (!this._activeDirector) {
      return undefined;
    }

    const addressData = this._directorAddressStore.tryGetForDirector(this._activeDirector.id);
    if (!addressData) {
      return undefined;
    }

    // Convert AddressData to AddressFormValue
    return {
      premise: addressData.premise,
      street: addressData.street,
      town: addressData.town,
      postcode: addressData.postcode,
      externalAddressId: addressData.externalAddressId,
      id: addressData.id,
      county: addressData.county,
      isOwnedByCompany: addressData.isOwnedByCompany,
      occupierName: addressData.occupierName,
      occupierDeclaration: addressData.occupierDeclaration,
      isUsualResidentalAddress: addressData.isUsualResidentalAddress,
      asicApprovalNumber: addressData.asicApprovalNumber,
      country: addressData.country,
    };
  }

  getDirectorAddress(directorId: string): AddressFormValue | undefined {
    const addressData = this._directorAddressStore.tryGetForDirector(directorId);
    if (!addressData) {
      return undefined;
    }
    // Convert AddressData to AddressFormValue
    return {
      premise: addressData.premise,
      street: addressData.street,
      town: addressData.town,
      postcode: addressData.postcode,
      externalAddressId: addressData.externalAddressId,
      id: addressData.id,
      county: addressData.county,
      isOwnedByCompany: addressData.isOwnedByCompany,
      occupierName: addressData.occupierName,
      occupierDeclaration: addressData.occupierDeclaration,
      isUsualResidentalAddress: addressData.isUsualResidentalAddress,
      asicApprovalNumber: addressData.asicApprovalNumber,
      country: addressData.country,
    };
  }

  // File upload handling
  async uploadProofOfAddress(file: File): Promise<string> {
    try {
      runInAction(() => {
        this._error = undefined;
      });

      // Upload file using FileShareClient
      const uploadResult = await this._fileShareClient.uploadFile(file);

      runInAction(() => {
        this._uploadedProofFile = file;
        this._uploadedProofFileId = uploadResult.id;
      });

      // Track successful upload
      this._analyticsManager.event("rejection_flow_proof_uploaded", {
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        upload_id: uploadResult.id,
        incorporation_id: this._incorporationStore.incorporation?.id,
      });

      return uploadResult.id;
    } catch (error) {
      runInAction(() => {
        this._error = "Failed to upload proof of address. Please try again.";
      });

      // Track upload error
      this._analyticsManager.event("rejection_flow_proof_upload_error", {
        error: error instanceof Error ? error.message : "Unknown error",
        file_name: file.name,
        incorporation_id: this._incorporationStore.incorporation?.id,
      });

      throw error;
    }
  }

  isAllDirectorsVerified(): boolean {
    return this._extendedDirectorsList.every((director) => director.isVerified);
  }

  private async markVerifiedAndMoveNext(): Promise<void> {
    const directorIndex = this._extendedDirectorsList.findIndex((d) => d.id === this._activeDirector?.id);
    if (directorIndex !== -1) {
      runInAction(() => {
        // Create a new array with the updated director to trigger MobX reactivity
        this._extendedDirectorsList = this._extendedDirectorsList.map((director, index) =>
          index === directorIndex ? { ...director, isVerified: true } : director,
        );
      });

      // Track director verification
      this._analyticsManager.event("rejection_flow_director_verified", {
        incorporation_id: this._incorporationStore.incorporation?.id,
        director_index: directorIndex + 1,
        total_directors: this._extendedDirectorsList.length,
        remaining_directors: this._extendedDirectorsList.filter((d) => !d.isVerified).length,
      });
    }

    if (this.isAllDirectorsVerified()) {
      // Track flow completion
      this._analyticsManager.event("rejection_flow_completed", {
        incorporation_id: this._incorporationStore.incorporation?.id,
        total_directors: this._extendedDirectorsList.length,
      });
      await this._registerStepStore.goNextQuestion();
      void runInAction(async () => {
        this._activeStep = RejectAddressStep.List;
        this._activeDirector = null;

        if (!this._issuesStore.hasIssues()) {
          this._analyticsManager.googleEvent("rejection_application_sent");
          await this._incorporationStore.createIncorporation();
        }
      });
      return;
    }
    this._activeStep = RejectAddressStep.List;
  }

  @FormSubmitMethod()
  async onSubmit(data: DirectorAddressFormTypes): Promise<void> {
    if (this._activeStep === RejectAddressStep.Address && this._activeDirector) {
      // For rejected application, we need to update the company details again in check step
      if (data.attachmentFileId && this._activeDirector.companyId) {
        this._analyticsManager.event("rejection_flow_address_submitted", {
          incorporation_id: this._incorporationStore.incorporation?.id,
          submission_type: "attachment",
          director_index: this._extendedDirectorsList.findIndex((d) => d.id === this._activeDirector?.id) + 1,
          total_directors: this._extendedDirectorsList.length,
        });

        await this._attachmentStore.attachFileInfo(
          this._activeDirector.companyId,
          data.attachmentFileId,
          this._activeDirector.id,
        );
        // Mark director as verified in local list
        await this.markVerifiedAndMoveNext();
      } else if (data.address) {
        this._analyticsManager.event("rejection_flow_address_submitted", {
          incorporation_id: this._incorporationStore.incorporation?.id,
          submission_type: "address_update",
          director_index: this._extendedDirectorsList.findIndex((d) => d.id === this._activeDirector?.id) + 1,
          total_directors: this._extendedDirectorsList.length,
        });

        await this._directorStore.updateAddress(this._activeDirector.id, data.address);
        await this.markVerifiedAndMoveNext();
      } else {
        throw new FormError("No address or attachment provided");
      }
    } else {
      if (!this._activeDirector) {
        throw new FormError("No active director to submit");
      }
      throw new FormError("Cannot submit form at this step");
    }
  }

  getDirectorId(): string {
    if (!this._directorId) {
      throw new FormError("No director's id");
    }

    return this._directorId;
  }

  getDirectorFirstName(): string {
    if (!this._activeDirector) {
      throw new FormError("No active director found!");
    }
    return this._activeDirector.firstName || "";
  }

  isSkipSave(): boolean {
    if (this.activeStep === RejectAddressStep.Address) {
      return true;
    }
    return false;
  }

  goBack(): void {
    const previousStep = STEPS[STEPS.indexOf(this.activeStep) - 1];
    this._analyticsManager.event("rejection_flow_navigation", {
      action: "back",
      from_step: this.activeStep,
      to_step: previousStep,
      incorporation_id: this._incorporationStore.incorporation?.id,
      verified_directors_count: this._extendedDirectorsList.filter((d) => d.isVerified).length,
      total_directors: this._extendedDirectorsList.length,
    });
    this.activeStep = previousStep as unknown as RejectAddressStep;

    // Notify Hotjar about step change for proper journey tracking
    if (typeof window !== "undefined" && window.hj && previousStep) {
      const virtualPath = `${window.location.pathname}/rejection-address/${previousStep.toLowerCase()}`;
      window.hj("stateChange", virtualPath);
    }
  }

  goForward(): void {
    this._goForward();
  }

  private _goForward(): void {
    const nextStep = STEPS[STEPS.indexOf(this.activeStep) + 1] as unknown as RejectAddressStep;
    this.activeStep = nextStep;

    // Notify Hotjar about step change for proper journey tracking
    if (typeof window !== "undefined" && window.hj) {
      const virtualPath = `${window.location.pathname}/rejection-address/${nextStep.toLowerCase()}`;
      window.hj("stateChange", virtualPath);
    }
  }
}
